import React, { FC, Ref, ReactElement } from 'react';
import classnames from 'classnames';

import { Button, ButtonVariant } from '@/shared/components/Button/Button';
import { Modal } from '@/shared/components/Modal/Modal';
import type { FullscreenOpenOtherTabProps } from '@/shared/types/notifications';

import { ControlButton } from './ControlButton';
import { getPrimaryButtonLinkParams } from './utils/getPrimaryButtonLinkParams';
import styles from './FullscreenNotification.module.css';

type Props = {
    foregroundImage: string;
    backgroundImage: string;
    contentLabel: string;
    closeButtonText: string;
    onCloseButtonClick: () => void;

    overlayClassName?: string;
    primaryButtonText?: string;
    primaryButtonClassName?: string;
    primaryButtonType?: ButtonVariant;
    primaryButtonIcon?: ReactElement;
    secondaryButtonText?: string;
    isHorizontalView?: boolean;
    isPrimaryButtonDisabled?: boolean;

    setElementRef?: Ref<HTMLDivElement>;
    onPrimaryButtonClick?: () => void;
    onSecondaryButtonClick?: () => void;
    modalParentSelector?: () => HTMLElement;
    imageAlt?: string;
    actionId?: string;
    isButtonsFocusable?: boolean;
} & FullscreenOpenOtherTabProps;

const HORIZONTAL_FG_SIZES = { x1: '1024x768', x2: '2048x1536' };
const VERTICAL_FG_SIZES = { x1: '460x390', x2: '920x780' };
const HORIZONTAL_BG_SIZES = { x1: '1024x768', x2: '2048x1536' };
const VERTICAL_BG_SIZES = { x1: '540x620', x2: '1080x1240' };

const getResizedImageUrl = (url: string, sizes: { x1: string; x2: string }): string => {
    const size = typeof window !== 'undefined' && window.devicePixelRatio > 1 ? sizes.x2 : sizes.x1;
    return url.replace('orig', size);
};

export const FullscreenNotification: FC<Props> = ({
    setElementRef,
    overlayClassName,
    foregroundImage,
    backgroundImage,
    primaryButtonText,
    primaryButtonClassName,
    primaryButtonType = 'accent',
    primaryButtonIcon,
    secondaryButtonText,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
    onCloseButtonClick,
    modalParentSelector,
    contentLabel,
    closeButtonText,
    isHorizontalView = false,
    imageAlt,
    isButtonsFocusable = false,
    isPrimaryButtonDisabled = false,
    ...restProps
}) => {
    const { primaryButtonHref, primaryButtonRel, primaryButtonTarget } =
        getPrimaryButtonLinkParams(restProps);

    const bgSizes = isHorizontalView ? HORIZONTAL_BG_SIZES : VERTICAL_BG_SIZES;
    const fgSizes = isHorizontalView ? HORIZONTAL_FG_SIZES : VERTICAL_FG_SIZES;
    const backgroundImageValue = `url('${getResizedImageUrl(backgroundImage, bgSizes)}')`;
    const foregroundImageUrl = getResizedImageUrl(foregroundImage, fgSizes);

    return (
        <Modal
            overlayClassName={classnames(styles.overlay, overlayClassName)}
            parentSelector={modalParentSelector}
            contentLabel={contentLabel}
            className={styles.overrideModal}
            closeIcon={false}
            isHideOnlyByCloseIcon
        >
            <div
                ref={setElementRef}
                className={styles.root}
                style={{
                    backgroundImage: backgroundImageValue,
                }}
            >
                <div
                    className={classnames(styles.container, {
                        [styles.horizontalView]: isHorizontalView,
                    })}
                >
                    <img
                        className={classnames(styles.foregroundImage, {
                            [styles.horizontalView]: isHorizontalView,
                        })}
                        src={foregroundImageUrl}
                        alt={imageAlt}
                    />
                    <div
                        className={classnames(styles.buttonContainer, {
                            [styles.horizontalView]: isHorizontalView,
                        })}
                    >
                        {onPrimaryButtonClick ? (
                            <Button
                                className={classnames(styles.primaryButton, primaryButtonClassName)}
                                variant={primaryButtonType}
                                size={48}
                                onClick={
                                    !isPrimaryButtonDisabled ? onPrimaryButtonClick : undefined
                                }
                                startIcon={primaryButtonIcon}
                                tabIndex={isButtonsFocusable ? 0 : -1}
                                aria-hidden={!isButtonsFocusable}
                                {...(primaryButtonHref
                                    ? {
                                          as: 'a' as const,
                                          href: primaryButtonHref,
                                          target: primaryButtonTarget,
                                          rel: primaryButtonRel,
                                      }
                                    : {})}
                            >
                                {primaryButtonText}
                            </Button>
                        ) : null}
                        {onSecondaryButtonClick ? (
                            <Button
                                variant={isHorizontalView ? 'secondary' : 'secondaryTransparent'}
                                theme="dark"
                                size={48}
                                onClick={onSecondaryButtonClick}
                                isFullWidth={!isHorizontalView}
                                tabIndex={isButtonsFocusable ? 0 : -1}
                                aria-hidden={!isButtonsFocusable}
                            >
                                {secondaryButtonText}
                            </Button>
                        ) : null}
                    </div>
                </div>
                <ControlButton onClick={onCloseButtonClick} aria-label={closeButtonText} />
            </div>
        </Modal>
    );
};
